import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithAuth } from "services/common/rtkQueryConfig";

export const dashboardApi = createApi({
  reducerPath: "dashboardApi",
  baseQuery: baseQueryWithAuth,
  tagTypes: ["dashboard", "document", "document-dealApplication"],
  endpoints: (build) => ({
    getDashboardItems: build.query<DashboardItemsResponseDto, { dealApplicationId: string; userId: string }>({
      query: ({ dealApplicationId, userId }) => `/dashboard/nhDealApplication-Id/${dealApplicationId}/userId/${userId}/outstanding-items`,
      providesTags: ["dashboard", "document", "document-dealApplication"],
    }),
    getNeedsListV1: build.query<PendingItem[], { nhDealApplicationId: string; userId: string }>({
      query: ({ nhDealApplicationId, userId }) => `/dashboard/nhDealApplicationId/${nhDealApplicationId}/userId/${userId}/conditions`,
      providesTags: ["dashboard", "document", "document-dealApplication"],
    }),
    getNeedsListV2: build.query<PendingItem[], { dealApplicationId: string; userId: string }>({
      query: ({ dealApplicationId, userId }) => `/needs/deal-application/${dealApplicationId}/user/${userId}/needs`,
      providesTags: ["dashboard", "document", "document-dealApplication"],
      keepUnusedDataFor: 0,
    }),
    setStatusFlagOfUser: build.mutation<void, string>({
      query: (id) => ({
        url: `/dashboard/nhDealApplication-Id/${id}`,
        method: "PATCH",
      }),
      invalidatesTags: ["dashboard"],
    }),
    setPalStatusFlagOfUser: build.mutation<void, string>({
      query: (id) => ({
        url: `/dashboard/deal-application/${id}/pal-status-flag`,
        method: "PATCH",
      }),
      invalidatesTags: ["dashboard"],
    }),
    uploadDocumentsWithSpecificCondition: build.mutation<string, FormData>({
      query: (body: FormData) => ({
        url: `/dashboard/UploadDocumentsWithSpecificConditionAsync`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["document"],
    }),
    uploadDocumentsWithSpecificConditionForDealApplication: build.mutation<string, FormData>({
      query: (body: FormData) => ({
        url: `/dashboard/deal-application/condition`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["document"],
    }),
    uploadDocumentsApplication: build.mutation<string, FormData>({
      query: (body: FormData) => ({
        url: `/dashboard/UploadDocumentsApplicationAsync`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["document"],
    }),
    uploadDocumentsForDealApplication: build.mutation<string, FormData>({
      query: (body: FormData) => ({
        url: `/dashboard/UploadDocuments/deal-application`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["document-dealApplication"],
    }),
    updateDocumentIds: build.mutation<boolean, DocumentSyncForAppIdRequest>({
      query: ({ applicationId, borrowerId, documentUpdateDto }: DocumentSyncForAppIdRequest) => ({
        url: `/dashboard/UpdateDocumentIds/${applicationId}/${borrowerId}`,
        method: "POST",
        body: documentUpdateDto,
      }),
      invalidatesTags: ["document"],
    }),
    updateDocumentIdsForDealApplication: build.mutation<boolean, DocumentSyncForDealAppIdRequest>({
      query: ({ dealApplicationId, borrowerId, documentUpdateDto }: DocumentSyncForDealAppIdRequest) => ({
        url: `/dashboard/UpdateDocumentIds/deal-application/${dealApplicationId}/${borrowerId}`,
        method: "POST",
        body: documentUpdateDto,
      }),
      invalidatesTags: ["document-dealApplication"],
    }),
    deleteDocument: build.mutation<boolean, FormData>({
      query: (formData: FormData) => ({
        url: "/dashboard/DeleteDocument",
        method: "DELETE",
        body: formData,
      }),
      invalidatesTags: ["document"],
    }),
    deleteDocumentForDealApplication: build.mutation<boolean, FormData>({
      query: (formData: FormData) => ({
        url: "/dashboard/DeleteDocument/deal-application",
        method: "DELETE",
        body: formData,
      }),
      invalidatesTags: ["document-dealApplication"],
    }),
    deleteFailedDocuments: build.mutation<void, string>({
      query: (nhDealApplicationId: string) => ({
        url: `/dashboard/nh-deal-application-id/${nhDealApplicationId}/documents`,
        method: "DELETE",
      }),
    }),
    retrieveDocuments: build.query<RetrieveDocumentsResponseDto[], string>({
      query: (applicationId) => `/dashboard/RetrieveDocumentsAsync/${applicationId}`,
      keepUnusedDataFor: 0.2,
    }),
    retrieveNeeds: build.query<RetrieveNeedDto[], string>({
      query: (dealApplicationId) => `/Needs/deal-application/${dealApplicationId}/needs-with-details`,
      keepUnusedDataFor: 0.2,
    }),

    retrieveDocumentsForDealApplication: build.query<RetrieveDocumentsResponseDto[], string>({
      query: (applicationId) => `/dashboard/deal-application/${applicationId}`,
      keepUnusedDataFor: 0.2,
    }),
    retrieveDocumentContent: build.query<RetrieveDocumentContent, string>({
      query: (fileId) => `/dashboard/RetrieveDocumentContentAsync/${fileId}`,
    }),
    retrievePalContent: build.query<RetrievePalResponseDto[], ReteievePalRequestDto>({
      query: ({ nhDealApplicationId, loanId }: ReteievePalRequestDto) =>
        `/dashboard/nh-dealapplication-id/${nhDealApplicationId}/loan/${loanId}/approval-letters`,
    }),
    maticResponse: build.mutation<string, { id: string; userId: string }>({
      query: ({ id, userId }) => ({
        url: `/dashboard/nhDealApplication-id/${id}/userId/${userId}/redirect-matic`,
        method: "POST",
        responseHandler: (response) => response.text(),
      }),
    }),
    getDashboardBanner: build.query<DashboardAdDto, string>({
      query: (id) => `/dashboard/nhDealApplication-id/${id}/ad`,
    }),
    checkCreditScore: build.query<boolean, { nhDealApplicationId: string; prospectId: string }>({
      query: ({ nhDealApplicationId, prospectId }) => `/dashboard/deal-application/${nhDealApplicationId}/prospect/${prospectId}/credit-score`,
    }),
    createDashboardSession: build.mutation<void, { nhDealApplicationId: string; userId: string; dashboardSession: DashboardSession }>({
      query: ({ nhDealApplicationId, userId, dashboardSession }) => ({
        url: `/dashboard/nh-dealapplication-id/${nhDealApplicationId}/user/${userId}/session-start`,
        method: "POST",
        body: dashboardSession,
      }),
    }),
    updatePartnerSession: build.mutation<void, { nhDealApplicationId: string; userId: string; partnerAdId: string }>({
      query: ({ nhDealApplicationId, userId, partnerAdId }) => ({
        url: `/dashboard/nh-dealapplication-id/${nhDealApplicationId}/user/${userId}/partner-session/${partnerAdId}`,
        method: "POST",
      }),
    }),
    UpdateTaskMessage: build.mutation<void, { nhDealApplicationId: string; conditionId: string; userId: string }>({
      query: ({ nhDealApplicationId, conditionId, userId }) => ({
        url: `/dashboard/nh-dealapplication-id/${nhDealApplicationId}/condition/${conditionId}/user/${userId}/update-task`,
        method: "POST",
      }),
    }),
    uploadDocumentsWithSpecificNeed: build.mutation<string, FormData>({
      query: (body: FormData) => ({
        url: `/needs/upload-need-document`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["document-dealApplication"],
    }),
    createNeeds: build.mutation<boolean, { dealApplicationId: string; userId: string; isInterimDashboard: boolean }>({
      query: ({ dealApplicationId, userId, isInterimDashboard }) => ({
        url: `/needs/deal-application/${dealApplicationId}/user/${userId}/interim/${String(isInterimDashboard)}`,
        method: "POST",
      }),
    }),
    deleteNeedDocument: build.mutation<string, { dealApplicationId: string; needId: string; documentId: string }>({
      query: ({ dealApplicationId, needId, documentId }) => ({
        url: `/needs/${needId}/deal-application/${dealApplicationId}/document/${documentId}/`,
        method: "DELETE",
      }),
    }),
    viewedDocumentsLog: build.mutation<void, { userId: string; documentIds: string[] }>({
      query: ({ userId, documentIds }) => ({
        url: `CyberSecurityAudit/userId/${userId}/viewed-documents`,
        method: "POST",
        body: documentIds,
      }),
    }),
    downloadedDocumentsLog: build.mutation<void, { userId: string; documentIds: string[] }>({
      query: ({ userId, documentIds }) => ({
        url: `CyberSecurityAudit/userId/${userId}/downloaded-documents`,
        method: "POST",
        body: documentIds,
      }),
    }),
    setEdisclosureStatusToUnderReview: build.mutation<void, { dealApplicationId: string; needId: string }>({
      query: ({ dealApplicationId, needId }) => ({
        url: `/needs/${needId}/deal-application/${dealApplicationId}/status/under-review`,
        method: "PATCH",
      }),
    }),
  }),
});

export const {
  useGetDashboardItemsQuery,
  useGetNeedsListV1Query,
  useGetNeedsListV2Query,
  useSetStatusFlagOfUserMutation,
  useUploadDocumentsWithSpecificConditionMutation,
  useUploadDocumentsApplicationMutation,
  useUploadDocumentsForDealApplicationMutation,
  useUpdateDocumentIdsMutation,
  useUpdateDocumentIdsForDealApplicationMutation,
  useUploadDocumentsWithSpecificConditionForDealApplicationMutation,
  useDeleteDocumentMutation,
  useDeleteDocumentForDealApplicationMutation,
  useDeleteFailedDocumentsMutation,
  useRetrieveDocumentsQuery,
  useRetrieveNeedsQuery,
  useRetrieveDocumentsForDealApplicationQuery,
  useRetrieveDocumentContentQuery,
  useRetrievePalContentQuery,
  useMaticResponseMutation,
  useGetDashboardBannerQuery,
  useSetPalStatusFlagOfUserMutation,
  useCheckCreditScoreQuery,
  useCreateDashboardSessionMutation,
  useUpdatePartnerSessionMutation,
  useUpdateTaskMessageMutation,
  useUploadDocumentsWithSpecificNeedMutation,
  useDeleteNeedDocumentMutation,
  useCreateNeedsMutation,
  useViewedDocumentsLogMutation,
  useDownloadedDocumentsLogMutation,
  useSetEdisclosureStatusToUnderReviewMutation,
} = dashboardApi;
export interface DashboardItemsResponseDto {
  loanStatus: LoanStatus;
  propertyInfo: PropertyInfo;
  closingDate?: Date;
  isNew: boolean;
  isPalViewed: boolean;
  isUqualSelfReferralDone?: boolean;
  isAwaitingNeedsCreation?: boolean;
  isApplicationFormRequired?: boolean;
}

export type LoanStatusType = "Started" | "Processing" | "Underwriting" | "Approval" | "FinalReview" | "Close" | "Funded" | "Unknown";

export type LoanSubStatusType = "UnderReview" | "ConditionallyApproved";

interface DashboardItemsRequestDto {
  userId: string;
  inquiryId: string;
}

interface DocumentUpdateDto {
  isInitialDocumentUploaded: boolean;
}

interface DocumentSyncForAppIdRequest {
  applicationId: string;
  borrowerId: string;
  documentUpdateDto: DocumentUpdateDto;
}

interface DocumentSyncForDealAppIdRequest {
  dealApplicationId: string;
  borrowerId: string;
  documentUpdateDto: DocumentUpdateDto;
}

interface DashboardAdDto {
  image: string;
  logo: string;
  imageText: string;
  description1: string;
  description2: string;
}

export interface LoanStatus {
  status: LoanStatusType;
  subStatus?: LoanSubStatusType;
}

export interface PropertyInfo {
  street: string;
  city: string;
  state: string;
  zipCode: string;
  loanNumber: string;
  loanAmount: number;
  loanType: string;
}

export interface PendingItem {
  title: string;
  status: "New" | "Submitted" | "Error" | "Pending Approval" | "Returned" | "needsAttention" | "Completed" | "underReview";
  id: string;
  note?: string;
  needStatus?: NeedStatus;
  documents?: Document[];
  needType?: NeedType;
  isEnabled?: boolean;
  lat?: string;
  packageId?: string;
}

export interface AnyDocumentvalues {
  id: number;
  fileName: string;
  timeZone: string;
}

export interface RetrieveDocumentContent {
  fileContents: string;
  contentType: string;
}
export interface DashboardSession {
  dashboardSessionId: string;
  dashboardSessionStartTimestamp: string | null;
  dashboardSessionEndTimestamp: string | null;
}
export interface RetrieveDocumentsResponseDto {
  fileId: string;
  name: string;
  documentPath: string;
  dateCreated: string;
  category: string;
  userName?: string;
  invalidReason?: string;
  detailedExplanation?: string;
  userNotified?: boolean;
  status?: DocumentStatus;
}

export interface DocumentsRequestDto {
  applicationId?: string;
}

export interface DocumentsRequestForDealApplicationDto {
  dealApplicationId?: string;
}

export interface RetrievePalResponseDto {
  palInstanceId: string;
  createdDate?: string;
  documentContent?: RetrievePalContent;
  documents: Document[];
}
interface RetrievePalContent {
  fileContents: string;
  contentType: string;
  name: string;
}

export interface ReteievePalRequestDto {
  nhDealApplicationId: string;
  loanId: string;
}

export interface GetConditionsRequestDto {
  nhDealApplicationId: string;
  isNeedsListV2Enabled: boolean;
}

export interface RetrieveNeedDto {
  id: string;
  borrowerId: string; // Null for "both borrowers"
  title: string;
  isDefault: boolean;
  documents: Document[];
}

export interface Document {
  id: string;
  uploadedDateTime: string;
  docName: string;
  uploader: Uploader;
  invalidReason?: string;
  detailedExplanation?: string;
  userNotified?: boolean;
  status?: DocumentStatus;
}

interface Uploader {
  firstName: string;
  lastName: string;
  emailAddress: string;
}

export enum NeedStatus {
  Unknown = "Unknown",
  ReviewNeeded = "ReviewNeeded",
  PendingDocuments = "PendingDocuments",
  Satisfied = "Satisfied",
}

export enum DocumentStatus {
  PendingReview = "PendingReview",
  Accepted = "Accepted",
  Rejected = "Rejected",
}

export enum NeedType {
  Needs = "needs",
  ESign = "eSign",
}
